import {
    AppIntegrationsRepository,
    MeRepository,
    LicensesRepository,
    DevicesRepository,
} from 'common/repositories/index';
/**
 * License Processor - licenses grouped by userId
 */

// these are the licenses grouped by userId and state
let processedLicenses = [];

const observableProcessedLicenses = new Rx.BehaviorSubject(processedLicenses);
const subjectLicensesRepository = LicensesRepository.getObservable();

/**
 * @ngdoc service
 * @name processors.LicensesProcessor
 *
 * @description
 * This class is used to process licenses.
 */
const LicensesProcessor = {

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#onNext
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * Processing made when the licenses array from the repository changes.
     *
     * @param {Array} data The modified licenses array
     *
     */
    onNext(data) {

        processedLicenses = [];
        data.forEach(license => this.addLicense(license));
        observableProcessedLicenses.onNext(processedLicenses);

    },

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#addLicense
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * Add license based on its userId
     *
     * @param {Object} license A license object
     *
     */
    addLicense(license) {

        const userId = license.relationships.user.data.id;
        processedLicenses[userId] = processedLicenses[userId] || [];
        processedLicenses[userId].push(license);

    },

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#getObservable
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * This function can be used to get an observable object for the processed licenses.
     * @returns {Object} an observable object for processed licenses.
     */
    getObservable() {

        return observableProcessedLicenses;

    },

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#calculateLicensesInfo
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * Calculates custom info for each license.
     *
     * @param {int} userId The user id
     * @param {string} deviceTypeFilter A device filter
     * @param {string} serviceFilter A service filter
     *
     *
     * @returns {Object} Licenses info grouped by each license id.
     */
    calculateLicensesInfo(userId, deviceTypeFilter, serviceFilter) {

        const licensesInfo = {};
        const devActivations = {};
        const userLicenses = processedLicenses[userId];

        if (userLicenses && userLicenses.length > 0) {

            userLicenses.forEach((licenseItem) => {

                const licenseApp = AppIntegrationsRepository
                    .getById(licenseItem.relationships.app.data.id);
                const expireDate = new Date(licenseItem.attributes.expiration_date);

                if (!angular.isUndefined(licenseApp) && expireDate >= new Date().getTime()) {

                    let skipLicense = licenseItem.attributes.type === 'eval';

                    if (!angular.isUndefined(deviceTypeFilter) && deviceTypeFilter !== null) {

                        deviceTypeFilter = deviceTypeFilter.toLowerCase();


                        if (licenseApp.attributes.custom.manifest.devices &&
                            !_.contains(
                                licenseApp.attributes.custom.manifest.devices,
                                deviceTypeFilter,
                            )
                        ) {

                            skipLicense = true;

                        }

                    }

                    const appIncludes = AppIntegrationsRepository
                        .getIncludes(userId, licenseApp.id);

                    if (!angular.isUndefined(serviceFilter) && serviceFilter !== null) {

                        if (appIncludes.components.length > 1 ||
                            appIncludes.components[0] !== serviceFilter) {

                            skipLicense = true;

                        }

                    }

                    if (!skipLicense) {

                        let licId = licenseItem.id;
                        let isActivation = false;
                        let deviceType = null;

                        if (licenseItem.relationships.license) {

                            licId = licenseItem.relationships.license.data.id;
                            isActivation = true;

                            if (licenseItem.relationships.device) {

                                const devActivationsId =
                                    devActivations[licenseItem.relationships.device.data.id];

                                if (angular.isUndefined(devActivationsId)
                                ) {

                                    devActivations[licenseItem.relationships.device.data.id] =
                                        true;

                                } else {

                                    isActivation = false;

                                }
                                const activatedDevice = DevicesRepository
                                    .getById(licenseItem.relationships.device.data.id);
                                if (activatedDevice) {

                                    deviceType = activatedDevice.attributes.type.toLowerCase();

                                }

                            }

                        }

                        if (!licensesInfo[licId]) {

                            let maxActivations = licenseItem.attributes.devices_limit;
                            let computerActivation = 0;
                            let mobileActivation = 0;

                            if (isActivation) {

                                maxActivations -= 1;
                                if (deviceType === 'pc' || deviceType === 'laptop') {

                                    computerActivation += 1;

                                } else {

                                    mobileActivation += 1;

                                }

                            }

                            licensesInfo[licId] = {
                                numberOfAvailableActivations: maxActivations,
                                numberOfActivationsByDevice: {
                                    computer: computerActivation,
                                    mobile: mobileActivation,
                                },
                                numberOfTotalActivations: licenseItem.attributes.devices_limit,
                                licInfo: {
                                    id: licId,
                                    expirationDate: licenseItem.attributes.expiration_date,
                                    runtime: licenseItem.attributes.runtime,
                                    runtimeUnit: licenseItem.attributes.runtime_unit,
                                },
                                appInfo: {
                                    name: licenseApp.attributes.custom.manifest.name,
                                    id: licenseApp.id,
                                    devices: licenseApp.attributes.custom.manifest.devices,
                                    includes: appIncludes,
                                },
                            };

                        } else if (isActivation) {

                            licensesInfo[licId].numberOfAvailableActivations -= 1;

                            if (licensesInfo[licId].numberOfAvailableActivations < 0) {

                                licensesInfo[licId].numberOfAvailableActivations = 0;

                            }

                            if (deviceType === 'pc' || deviceType === 'laptop') {

                                licensesInfo[licId].numberOfActivationsByDevice.computer += 1;

                            } else {

                                licensesInfo[licId].numberOfActivationsByDevice.computer += 1;

                            }

                        }

                    }

                }

            });

        }
        return licensesInfo;

    },

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#calculateAvailableActivations
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * Calculates:
     * * how many available license activations exist for each app and
     * * total available licence activations, for all apps
     *
     * @returns {Object} An object with two keys:
     * * availableActivations - the available license activations for each app
     * * totalAvailableActivations - the total available activations for all apps
     */
    calculateAvailableActivations() {

        const availableActivations = {};
        let totalAvailableActivations = 0;
        const licenses = this.calculateLicensesInfo(MeRepository.getMyId());
        const licenseKeys = Object.keys(licenses);
        licenseKeys.forEach((licenseKey) => {

            const licItem = licenses[licenseKey];
            const appId = licItem.appInfo.id;
            if (!availableActivations[appId]) {

                availableActivations[appId] = {};
                availableActivations[appId].name = licItem.appInfo.name;
                availableActivations[appId].activations = 0;

            }

            availableActivations[appId].activations += licItem.numberOfAvailableActivations;
            totalAvailableActivations += licItem.numberOfAvailableActivations;

        });

        return {
            availableActivations,
            totalAvailableActivations,
        };

    },

    /**
     * @ngdoc function
     * @name processors.LicensesProcessor#isProUser
     * @methodOf processors.LicensesProcessor
     * @function
     *
     * @description
     * Checks if an user has at least one pro license.
     *
     * @param {int} userId The user id
     *
     * @returns {Boolean} True if the user has at least one pro license, fase otherwise
     */
    isProUser(userId) {

        let isPro = false;
        if (processedLicenses[userId]) {

            processedLicenses[userId].find((license) => {

                if (license && license.attributes && license.attributes.type === 'paid') {

                    isPro = true;
                    return true;

                }

                return false;

            });
            return isPro;

        }
        return isPro;

    },

};

subjectLicensesRepository.subscribe(
    data => LicensesProcessor.onNext(data),
    () => { },
    () => { },
);

export default LicensesProcessor;



// WEBPACK FOOTER //
// ./scripts/common/repositoryProcessors/-RFC.licensesProcessor.js